import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
  inject,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { distinctUntilChanged, map, Observable, Subscription } from 'rxjs';
import { NgFormsManager } from '@ngneat/forms-manager';
import {
  ColorScheme,
  ColorSchemeService,
} from '@fantaz/shared/services/color-scheme';

interface PromoCodeModalData {
  submitting?: Observable<boolean>;
  closeOn?: Observable<boolean>;
  submit: (promoCode: string) => void;
  initialPromoCode?: string;
  source?: 'unknown' | 'header' | 'footer';
}

const PROMO_CODE_DIALOG_KEY = 'promocode';

@UntilDestroy()
@Component({
  selector: 'fantaz-promote-code-modal',
  templateUrl: './promote-code-modal.component.html',
  styleUrls: ['./promote-code-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromoteCodeModalComponent implements OnInit {
  form: FormGroup;
  formsManager: NgFormsManager = inject(NgFormsManager);
  onClose: Subscription;
  readonly ColorScheme = ColorScheme;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    readonly data: PromoCodeModalData,
    private dialogRef: MatDialogRef<PromoteCodeModalComponent>,
    private router: Router,
    private route: ActivatedRoute,
    private readonly colorSchemeService: ColorSchemeService,
  ) {
    this.colorSchemeService.init();
  }

  ngOnInit(): void {
    console.log('[ PromoCodeDialog ] Start');
    this.createForm();
    this.subscribeToSubmitting();
    this.subscribeToClose();
    this.addRouterParameter();
  }

  private createForm() {
    console.log('[ PromoCodeDialog ] Create Form');
    this.form = new FormGroup({
      promoCode: new FormControl('', [
        Validators.required,
        Validators.pattern(/^[a-zA-Z0-9]*$/),
      ]),
    });

    this.formsManager.upsert('promocode', this.form, {
      persistState: true,
    });

    if (this.data?.initialPromoCode) {
      this.form.patchValue({
        promoCode: this.data.initialPromoCode.toUpperCase(),
      });
    }
  }

  private subscribeToSubmitting() {
    console.log(
      '[ PromoCodeDialog ] Subscribe to form submit observable event',
    );
    if (this.data?.submitting) {
      this.data.submitting
        .pipe(
          untilDestroyed(this),
          map((submitting) => {
            if (submitting) {
              this.form.disable();
            } else {
              this.form.enable();
            }
          }),
        )
        .subscribe();
    }
  }

  private subscribeToClose() {
    console.log('[ PromoCodeDialog ] Subscribe to close observable event');
    if (this.data?.closeOn) {
      this.onClose = this.data.closeOn
        .pipe(untilDestroyed(this), distinctUntilChanged())
        .subscribe((value) => {
          if (value && value === true) {
            // remove subscription after first call
            this.onClose.unsubscribe();
            // close dialog
            this.closeModal('form-submit-success');
          }
        });
    }
  }

  private addRouterParameter() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        dialog: PROMO_CODE_DIALOG_KEY,
        source: this.data?.source || 'unknown',
        value: this.data?.initialPromoCode || null,
      },
      queryParamsHandling: 'merge',
    });
  }

  onFormSubmit() {
    const { promoCode } = this.form.value;

    this.form.markAsDirty();

    if (this.data?.submit && this.form.valid) {
      // force uppercase in case user entered lowercase
      this.data.submit(promoCode.toUpperCase());
    }
  }

  closeModal(source: string, event?: MouseEvent) {
    console.log('close', source);
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    // remove router parameter
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { dialog: null, promocode: null, source: null, value: null },
      queryParamsHandling: 'merge',
    });

    // clear form
    this.formsManager.destroy('promocode');

    // close dialog
    this.dialogRef.close({ source });
  }

  get colorScheme() {
    return this.colorSchemeService.get();
  }
}
